import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, ChevronDown, X, Check } from 'lucide-react';
import { deviceMatchesOS } from '../utils/devicePlatformMatch';

export function getDeviceId(d: any): string {
  return String(d?.device_id || d?.id || '');
}

function deviceLabel(d: any): string {
  const id = getDeviceId(d);
  const name = d?.name || id;
  const meta = [d?.os || d?.platform, d?.ip || d?.ip_address || d?.device_metadata?.ip_address]
    .filter(Boolean)
    .join(' · ');
  return meta ? `${name} (${meta})` : name;
}

function fuzzyMatch(haystack: string, needle: string): boolean {
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase().trim();
  if (!n) return true;
  if (h.includes(n)) return true;
  // simple subsequence fuzzy (e.g. "hei" → heisenberg)
  let i = 0;
  for (const ch of h) {
    if (ch === n[i]) i += 1;
    if (i >= n.length) return true;
  }
  return false;
}

function filterDevices(devices: any[], query: string, platformFilter?: string | null): any[] {
  let list = Array.isArray(devices) ? devices : [];
  if (platformFilter) {
    const plat = platformFilter.startsWith('plat-') ? platformFilter : `plat-${platformFilter}`;
    list = list.filter(
      (d) =>
        deviceMatchesOS(d, plat) ||
        deviceMatchesOS(d, platformFilter) ||
        String(d.platform || '').toLowerCase() === String(platformFilter).toLowerCase() ||
        String(d.os || '').toLowerCase().includes(String(platformFilter).replace(/^plat-/, '').toLowerCase())
    );
  }
  if (!query.trim()) return list;
  return list.filter((d) => {
    const blob = [
      d.name,
      getDeviceId(d),
      d.os,
      d.platform,
      d.ip,
      d.ip_address,
      d.serial_number,
      d.device_metadata?.hostname,
      d.device_metadata?.serial_number,
    ]
      .filter(Boolean)
      .join(' ');
    return fuzzyMatch(blob, query);
  });
}

type SingleProps = {
  mode?: 'single';
  devices: any[];
  value: string;
  onChange: (deviceId: string) => void;
  platformFilter?: string | null;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  emptyMessage?: string;
};

type MultiProps = {
  mode: 'multiple';
  devices: any[];
  value: string[];
  onChange: (deviceIds: string[]) => void;
  platformFilter?: string | null;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  emptyMessage?: string;
  maxHeightClass?: string;
};

export type DevicePickerProps = SingleProps | MultiProps;

/**
 * Searchable device picker (single dropdown or multi checkbox list).
 * Fuzzy-filters by name, id, OS, IP, serial.
 */
const DevicePicker: React.FC<DevicePickerProps> = (props) => {
  const {
    devices,
    platformFilter,
    placeholder = 'Search devices by name, ID, OS, IP…',
    disabled = false,
    className = '',
    emptyMessage = 'No matching devices.',
  } = props;

  const mode = props.mode || 'single';
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(
    () => filterDevices(devices, query, platformFilter),
    [devices, query, platformFilter]
  );

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (mode === 'multiple') {
    const value = (props as MultiProps).value || [];
    const onChange = (props as MultiProps).onChange;
    const maxHeightClass = (props as MultiProps).maxHeightClass || 'max-h-40';
    return (
      <div className={`space-y-2 ${className}`} ref={rootRef}>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="search"
            value={query}
            disabled={disabled}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            className="w-full pl-8 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50"
          />
        </div>
        <div className={`${maxHeightClass} overflow-y-auto border border-slate-300 rounded-lg p-2 space-y-1 bg-white`}>
          {filtered.length === 0 ? (
            <p className="text-xs text-slate-500 py-2 px-1">{emptyMessage}</p>
          ) : (
            filtered.map((d) => {
              const id = getDeviceId(d);
              const checked = value.includes(id);
              return (
                <label
                  key={id}
                  className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 rounded px-1.5 py-1"
                >
                  <input
                    type="checkbox"
                    disabled={disabled}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    checked={checked}
                    onChange={() =>
                      onChange(checked ? value.filter((x) => x !== id) : [...value, id])
                    }
                  />
                  <span className="text-sm text-slate-700 truncate flex-1">{d.name || id}</span>
                  <span className="text-[10px] text-slate-400 shrink-0">{d.os || d.platform || ''}</span>
                </label>
              );
            })
          )}
        </div>
        {value.length > 0 && (
          <p className="text-xs text-slate-500">{value.length} device(s) selected</p>
        )}
      </div>
    );
  }

  const value = (props as SingleProps).value || '';
  const onChange = (props as SingleProps).onChange;
  const selected = devices.find((d) => getDeviceId(d) === value);

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white text-left focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50"
      >
        <Search size={14} className="text-slate-400 shrink-0" />
        <span className={`flex-1 truncate ${selected ? 'text-slate-800' : 'text-slate-400'}`}>
          {selected ? deviceLabel(selected) : '-- Choose a device --'}
        </span>
        {value ? (
          <span
            role="button"
            tabIndex={0}
            className="p-0.5 rounded hover:bg-slate-100 text-slate-400"
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
              setQuery('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                onChange('');
              }
            }}
          >
            <X size={14} />
          </span>
        ) : (
          <ChevronDown size={14} className="text-slate-400 shrink-0" />
        )}
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <input
              autoFocus
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <ul className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-xs text-slate-500">{emptyMessage}</li>
            ) : (
              filtered.map((d) => {
                const id = getDeviceId(d);
                const active = id === value;
                return (
                  <li key={id}>
                    <button
                      type="button"
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-blue-50 ${
                        active ? 'bg-blue-50 text-blue-800' : 'text-slate-700'
                      }`}
                      onClick={() => {
                        onChange(id);
                        setOpen(false);
                        setQuery('');
                      }}
                    >
                      <span className="flex-1 truncate font-medium">{d.name || id}</span>
                      <span className="text-[10px] text-slate-400 shrink-0">
                        {[d.os || d.platform, d.ip || d.ip_address].filter(Boolean).join(' · ')}
                      </span>
                      {active && <Check size={14} className="text-blue-600 shrink-0" />}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DevicePicker;
